import customerService from '../../../services/customerService';
import filterService from '../../../services/filterService';

import {
    setCustomerLoading,
    setCustomerError,
    setCustomerList,
    setCustomerCurrent,
    setCustomerPagination,
    setCustomerSort,
    setCustomerFilters,
    setCustomerDefaultFilter,
    setCustomerSettings,
    clearCustomerCurrent,
    clearCustomerError,
    setCustomerFilterLoading,
    changeCustomerFilterCondition,
    addCustomerFilterCondition,
    updateCustomerFilterConditionRule,
    updateCustomerFilterConditionValue,
    removeCustomerFilterCondition,
    setCustomerSelected,
    resetCustomerFiltersAndPagination
} from './customerSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().customer.isSettingsInitialized) {
            dispatch(setCustomerLoading(true));
            const response = await customerService.getSettings();

            // Set the settings
            dispatch(setCustomerSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setCustomerDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setCustomerError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setCustomerLoading(false));
    }
};

export const fetchCustomers = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized
        const state = getState().customer;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setCustomerLoading(true));
        const response = await customerService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setCustomerList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchCustomers:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setCustomerError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCustomerLoading(false));
    }
};

export const fetchCustomer = (id) => async (dispatch) => {
    try {
        dispatch(setCustomerLoading(true));
        const response = await customerService.getOne(id);
        dispatch(setCustomerCurrent(response.data));
    } catch (error) {
        dispatch(setCustomerError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setCustomerLoading(false));
    }
};

export const createCustomer = (data) => async (dispatch) => {
    try {
        const response = await customerService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateCustomer = (id, data) => async (dispatch) => {
    try {
        const response = await customerService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteCustomer = (id) => async (dispatch) => {
    try {
        dispatch(setCustomerLoading(true));
        const response = await customerService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setCustomerError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCustomerLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setCustomerPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setCustomerSort(sort));
};

export const searchCustomers = (query) => (dispatch, getState) => {
    const currentFilters = getState().customer.filters;
    dispatch(setCustomerFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setCustomerDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeCustomerFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addCustomerFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateCustomerFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateCustomerFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeCustomerFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setCustomerFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().customer.settings;
            dispatch(setCustomerSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setCustomerFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setCustomerFilterLoading(false));
        dispatch(setCustomerError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setCustomerSelected(selectedRows));
    };
};

export const clearCurrentCustomer = () => {
    return (dispatch) => {
        dispatch(clearCustomerCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetCustomerFiltersAndPagination());
};